/**
 * Magic Forest background configuration
 * 
 * URL CONTRACT:
 * All media files are served under the /api/uploads/** namespace:
 * - Background presets: /api/uploads/backgrounds/presets/<filename>
 * - Custom backgrounds: /api/uploads/backgrounds/custom/<filename>
 * - Widget images: /api/uploads/widgets/<filename>
 * - Widget videos: /api/uploads/videos/<filename>
 * 
 * URLs are ALWAYS relative (no origin/domain prefix) to ensure
 * consistent behavior across all deployment environments.
 *
 * GRADIENT CONVENTION:
 * Gradient backgrounds are stored as "gradient:N" strings in backgroundUrl.
 * ForestBackground detects the prefix and renders CSS instead of loading an image.
 */

import { colors, hexToRgb } from './colors';

// Background tuning parameters
export const BG_DARK_OVERLAY = 0.1;
export const BG_VIGNETTE_MAX = 0.6;
export const BG_FOG_OPACITY = 0.1;

// Default preset background URLs (relative paths - served by nginx alias)
export const PRESET_BG_URLS = [
  '/api/uploads/backgrounds/presets/forest-01.jpg',
  '/api/uploads/backgrounds/presets/forest-02.jpg',
  '/api/uploads/backgrounds/presets/forest-03.jpg',
  '/api/uploads/backgrounds/presets/forest-04.jpg',
];

// Legacy: Mapping for slides (cycles through presets)
export const FOREST_BG_URLS = [
  ...PRESET_BG_URLS,
  ...PRESET_BG_URLS,  // Repeat to cover 10 slides
  ...PRESET_BG_URLS.slice(0, 2),
];

// ─── Background darken levels (moved from GlassCard.js) ─────────────

/** Black overlay intensity on top of background image */
export const BG_DARKEN_LEVELS = [
  { value: 0, label: 'None',   opacity: 0 },
  { value: 1, label: 'Subtle', opacity: 0.1 },
  { value: 2, label: 'Light',  opacity: 0.2 },
  { value: 3, label: 'Medium', opacity: 0.35 },
  { value: 4, label: 'Strong', opacity: 0.5 },
  { value: 5, label: 'Heavy',  opacity: 0.65 },
];

export const DEFAULT_BG_DARKEN = 0; // None (image shown as-is)

// ─── Background visual effects (BG FX) ──────────────────────────────
// Each effect has 6 discrete levels. When bgFx is undefined (old slides),
// DEFAULT_BG_FX matches the original hardcoded values exactly.

/** Dark overlay — uniform black film over the entire background */
export const BG_FX_OVERLAY_LEVELS = [
  { label: 'None',   opacity: 0 },
  { label: 'Subtle', opacity: 0.05 },
  { label: 'Light',  opacity: 0.1 },     // ← original hardcoded default
  { label: 'Medium', opacity: 0.15 },
  { label: 'Strong', opacity: 0.2 },
  { label: 'Heavy',  opacity: 0.3 },
];

/** Vignette — radial darkening from edges inward */
export const BG_FX_VIGNETTE_LEVELS = [
  { label: 'None',   opacity: 0 },
  { label: 'Subtle', opacity: 0.15 },
  { label: 'Light',  opacity: 0.3 },
  { label: 'Medium', opacity: 0.45 },
  { label: 'Strong', opacity: 0.6 },     // ← original hardcoded default
  { label: 'Heavy',  opacity: 0.75 },
];

/** Fog — white radial glow at center-bottom */
export const BG_FX_FOG_LEVELS = [
  { label: 'None',   opacity: 0 },
  { label: 'Subtle', opacity: 0.05 },
  { label: 'Light',  opacity: 0.1 },     // ← original hardcoded default
  { label: 'Medium', opacity: 0.15 },
  { label: 'Strong', opacity: 0.2 },
  { label: 'Heavy',  opacity: 0.3 },
];

/** Noise — SVG fractal noise texture overlay */
export const BG_FX_NOISE_LEVELS = [
  { label: 'None',   opacity: 0 },
  { label: 'Subtle', opacity: 0.04 },
  { label: 'Light',  opacity: 0.08 },    // ← original hardcoded default
  { label: 'Medium', opacity: 0.12 },
  { label: 'Strong', opacity: 0.16 },
  { label: 'Heavy',  opacity: 0.22 },
];

/** Default FX config — exactly matches original hardcoded behavior */
export const DEFAULT_BG_FX = {
  enabled: true,
  overlay: 2,    // Light (0.1)  — matches BG_DARK_OVERLAY
  vignette: 4,   // Strong (0.6) — matches BG_VIGNETTE_MAX
  fog: 2,        // Light (0.1)  — matches BG_FOG_OPACITY
  noise: 2,      // Light (0.08) — matches NoiseOverlay opacity
};

/** Resolve bgFx from slide data — fills missing fields with defaults */
export function resolveBgFx(bgFx) {
  if (!bgFx) return DEFAULT_BG_FX;
  return {
    enabled: bgFx.enabled !== false, // default true
    overlay: bgFx.overlay ?? DEFAULT_BG_FX.overlay,
    vignette: bgFx.vignette ?? DEFAULT_BG_FX.vignette,
    fog: bgFx.fog ?? DEFAULT_BG_FX.fog,
    noise: bgFx.noise ?? DEFAULT_BG_FX.noise,
  };
}

// ─── Solid background colors ────────────────────────────────────────
// Generated from colors.js — TRANSPOSED matrix:
//   Rows  = shade levels (50→950, light→dark)  — 11 rows
//   Cols  = Tailwind families in hue order      — 15 columns
// Compact vertical layout; coherent left-to-right hue progression per row.

/** Shade levels — each becomes one row */
export const SOLID_SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

/** Families in hue-wheel order — each becomes one column */
export const SOLID_FAMILY_ORDER = [
  'slate', 'gray', 'zinc', 'neutral', 'stone', // neutrals
  'red', 'orange', 'amber', 'yellow',           // warm
  'emerald', 'teal',                             // nature
  'sky', 'blue', 'indigo',                       // cool
  'violet', 'purple', 'pink', 'rose',            // creative
];

/**
 * Build one row: all families at a single shade level.
 * Each row reads left→right as a hue rainbow at fixed brightness.
 */
function buildShadeRow(shade) {
  return SOLID_FAMILY_ORDER.map(family => ({
    id: `${family}-${shade}`,
    hex: colors[family][shade],
    name: `${family.charAt(0).toUpperCase() + family.slice(1)} ${shade}`,
    family,
    shade,
  }));
}

/** All solid color rows — array of arrays, one per shade level */
export const SOLID_ROWS = SOLID_SHADES.map(buildShadeRow);

/** Flat array of all solid colors (for lookups) */
export const SOLID_BG_COLORS = SOLID_ROWS.flat();

/** Hex → color entry lookup (for resolving bgSolid hex → id) */
export const SOLID_BG_BY_HEX = Object.fromEntries(
  SOLID_BG_COLORS.map((c) => [c.hex, c])
);

// ─── Grain / noise texture for solid backgrounds ────────────────────

export const BG_GRAIN_LEVELS = [
  { label: 'None',   opacity: 0 },
  { label: 'Subtle', opacity: 0.05 },
  { label: 'Light',  opacity: 0.10 },
  { label: 'Medium', opacity: 0.18 },
  { label: 'Strong', opacity: 0.30 },
  { label: 'Heavy',  opacity: 0.50 },
];
export const DEFAULT_BG_GRAIN = 0;

// ─── Brightness compensation for solid backgrounds ──────────────────
// Darkens solid backgrounds to offset grain-induced brightening.

export const BG_COMPENSATION_LEVELS = [
  { label: 'None',   opacity: 0 },
  { label: '−1',     opacity: 0.04 },
  { label: '−2',     opacity: 0.08 },
  { label: '−3',     opacity: 0.14 },
  { label: '−4',     opacity: 0.22 },
  { label: '−5',     opacity: 0.35 },
];
export const DEFAULT_BG_COMPENSATION = 0;

// ─── Gradient backgrounds ───────────────────────────────────────────

/** Base tones — dark linear gradients */
export const GRADIENT_BASE_TONES = [
  { id: 'forest',   name: 'Forest',   angle: 160, colors: ['rgba(6,20,14,1)', 'rgba(10,32,20,1)', 'rgba(4,14,10,1)'], swatch: '#0a200e' },
  { id: 'ocean',    name: 'Ocean',    angle: 170, colors: ['rgba(3,10,24,1)', 'rgba(6,20,40,1)', 'rgba(2,8,20,1)'], swatch: '#061428' },
  { id: 'night',    name: 'Night',    angle: 160, colors: ['rgba(14,6,30,1)', 'rgba(24,12,44,1)', 'rgba(8,3,18,1)'], swatch: '#180c2c' },
  { id: 'earth',    name: 'Earth',    angle: 155, colors: ['rgba(18,14,8,1)', 'rgba(30,22,12,1)', 'rgba(12,10,6,1)'], swatch: '#1e160c' },
  { id: 'charcoal', name: 'Charcoal', angle: 165, colors: ['rgba(22,22,26,1)', 'rgba(34,34,40,1)', 'rgba(16,16,18,1)'], swatch: '#222228' },
  { id: 'ember',    name: 'Ember',    angle: 160, colors: ['rgba(24,10,4,1)', 'rgba(40,18,8,1)', 'rgba(16,6,2,1)'], swatch: '#281208' },
];

/** Accent colors for glow effects — sourced from color foundation */
export const GRADIENT_ACCENT_COLORS = [
  { id: 'emerald', name: 'Emerald', ...hexToRgb(colors.emerald[400]), hex: colors.emerald[400] },
  { id: 'gold',    name: 'Gold',    ...hexToRgb(colors.yellow[400]),  hex: colors.yellow[400] },
  { id: 'blue',    name: 'Blue',    ...hexToRgb(colors.sky[400]),     hex: colors.sky[400] },
  { id: 'violet',  name: 'Violet',  ...hexToRgb(colors.violet[400]), hex: colors.violet[400] },
  { id: 'rose',    name: 'Rose',    ...hexToRgb(colors.pink[500]),    hex: colors.pink[500] },
  { id: 'amber',   name: 'Amber',   ...hexToRgb(colors.orange[400]), hex: colors.orange[400] },
  { id: 'teal',    name: 'Teal',    ...hexToRgb(colors.teal[500]),    hex: colors.teal[500] },
  { id: 'white',   name: 'White',   ...hexToRgb(colors.white),       hex: colors.white },
  { id: 'black',   name: 'Black',   ...hexToRgb(colors.black),       hex: colors.black },
];

/** Recommended accent pairs per base tone (analogous / monochromatic) */
export const RECOMMENDED_ACCENTS = {
  forest:   { accent1: 'emerald', accent2: 'teal' },
  ocean:    { accent1: 'blue',    accent2: 'teal' },
  night:    { accent1: 'violet',  accent2: 'rose' },
  earth:    { accent1: 'gold',    accent2: 'amber' },
  charcoal: { accent1: 'white',   accent2: 'blue' },
  ember:    { accent1: 'amber',   accent2: 'rose' },
};

/** Gradient geometry modes */
export const GRADIENT_GEOMETRIES = [
  { id: 'radial', name: 'Radial' },
  { id: 'linear', name: 'Linear' },
];

/** Fog (white glow at bottom) intensity levels */
export const GRADIENT_FOG_LEVELS = [
  { value: 0, label: 'None',   opacity: 0 },
  { value: 1, label: 'Subtle', opacity: 0.05 },
  { value: 2, label: 'Light',  opacity: 0.1 },
  { value: 3, label: 'Medium', opacity: 0.15 },
  { value: 4, label: 'Strong', opacity: 0.22 },
  { value: 5, label: 'Heavy',  opacity: 0.3 },
];

/** Position map for accent glow placement */
export const GRADIENT_POSITIONS = [
  { id: 'tl', css: '20% 20%' },
  { id: 'tc', css: '50% 20%' },
  { id: 'tr', css: '80% 20%' },
  { id: 'ml', css: '20% 50%' },
  { id: 'mc', css: '50% 50%' },
  { id: 'mr', css: '80% 50%' },
  { id: 'bl', css: '20% 80%' },
  { id: 'bc', css: '50% 80%' },
  { id: 'br', css: '80% 80%' },
];

/** Position → angle mapping for linear and conic geometries */
const POSITION_TO_ANGLE = {
  tl: 135, tc: 180, tr: 225,
  ml: 90,  mc: 180, mr: 270,
  bl: 45,  bc: 0,   br: 315,
};

/** Default gradient config for new slides */
export const DEFAULT_GRADIENT = {
  enabled: false,
  base: 'forest',
  geometry: 'radial',
  accent1Color: 'emerald',
  accent1Intensity: 40,
  accent1Position: 'tr',
  accent2Enabled: false,
  accent2Color: 'teal',
  accent2Intensity: 25,
  accent2Position: 'bl',
  darken: 0,
  fog: 2,
};

/**
 * Build a CSS gradient string from a gradient config object.
 * Supports three geometry modes: radial (soft glow), linear (directional sweep),
 * conic (angular sweep from focal point).
 */
export function buildGradientCss(gradient) {
  if (!gradient) return null;

  const baseTone = GRADIENT_BASE_TONES.find(t => t.id === (gradient.base || 'forest'))
    || GRADIENT_BASE_TONES[0];
  const baseCss = `linear-gradient(${baseTone.angle}deg, ${baseTone.colors.join(', ')})`;
  const geometry = gradient.geometry || 'radial';

  const parts = [];

  /**
   * Build one accent layer in the chosen geometry.
   * @param {string} accentColorId
   * @param {number} intensity   0–100
   * @param {string} positionId  e.g. 'tr'
   * @param {string} radialSize  e.g. '900px 700px' (radial only)
   */
  function buildAccentCss(accentColorId, intensity, positionId, radialSize) {
    const color = GRADIENT_ACCENT_COLORS.find(c => c.id === accentColorId) || GRADIENT_ACCENT_COLORS[0];
    const opacity = (intensity / 100) * 0.35;
    const pos = GRADIENT_POSITIONS.find(p => p.id === positionId) || GRADIENT_POSITIONS[2];
    const angle = POSITION_TO_ANGLE[positionId] || 180;

    switch (geometry) {
      case 'linear':
        return `linear-gradient(${angle}deg, rgba(${color.r},${color.g},${color.b},${opacity.toFixed(3)}) 0%, rgba(${color.r},${color.g},${color.b},${(opacity * 0.3).toFixed(3)}) 35%, transparent 65%)`;

      default: // radial
        return `radial-gradient(${radialSize} at ${pos.css}, rgba(${color.r},${color.g},${color.b},${opacity.toFixed(3)}), transparent 55%)`;
    }
  }

  // Accent 1
  parts.push(buildAccentCss(
    gradient.accent1Color || 'emerald',
    gradient.accent1Intensity ?? 40,
    gradient.accent1Position || 'tr',
    '900px 700px'
  ));

  // Accent 2 (optional)
  if (gradient.accent2Enabled) {
    parts.push(buildAccentCss(
      gradient.accent2Color || 'teal',
      gradient.accent2Intensity ?? 25,
      gradient.accent2Position || 'bl',
      '700px 500px'
    ));
  }

  parts.push(baseCss);
  return parts.join(', ');
}

// Legacy preset gradients (kept for fallback rendering only)
export const GRADIENT_BACKGROUNDS = GRADIENT_BASE_TONES.slice(0, 3).map((base, i) => ({
  name: base.name,
  css: buildGradientCss({
    base: base.id,
    geometry: 'radial',
    accent1Color: ['emerald', 'blue', 'gold'][i],
    accent1Intensity: 40,
    accent1Position: 'tr',
    accent2Enabled: true,
    accent2Color: ['gold', 'emerald', 'emerald'][i],
    accent2Intensity: 25,
    accent2Position: 'bl',
  }),
}));

export const FALLBACK_GRADIENTS = GRADIENT_BACKGROUNDS.map(g => g.css);

// ─── Helpers ────────────────────────────────────────────────────────

/** Check if a backgroundUrl is a gradient reference */
export function isGradientUrl(url) {
  return typeof url === 'string' && url.startsWith('gradient:');
}

/** Extract gradient index from "gradient:N" string */
export function getGradientIndex(url) {
  if (!isGradientUrl(url)) return -1;
  return parseInt(url.split(':')[1], 10) || 0;
}

/** Get CSS for a gradient URL, with fallback */
export function getGradientCss(url) {
  const idx = getGradientIndex(url);
  if (idx >= 0 && idx < GRADIENT_BACKGROUNDS.length) {
    return GRADIENT_BACKGROUNDS[idx].css;
  }
  return GRADIENT_BACKGROUNDS[0].css; // fallback to first gradient
}
